import React from 'react';
import { Modal, Typography } from 'antd';
import { THEME_COLORS } from '../../utils/colors';

const { Title, Text, Paragraph } = Typography;

const TermsOfServiceModal = ({ open, onCancel }) => {
  return (
    <Modal
      title={
        <div style={{ textAlign: "center" }}>
          <Title
            level={3}
            style={{
              marginBottom: 4,
              fontWeight: 700,
              color: THEME_COLORS.DARK.text.primary,
              fontSize: '1.5rem'
            }}
          >
            Hooptie.io — Terms of Service
          </Title>
          <Text
            style={{
              color: THEME_COLORS.DARK.text.tertiary,
              fontSize: 12,
              lineHeight: 1.6
            }}
          >
            Last Updated: December 9, 2025
          </Text>
        </div>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      width={900}
      style={{ top: 20 }}
      closeIcon={<span style={{ color: THEME_COLORS.DARK.text.primary, fontSize: '20px' }}>×</span>}
      bodyStyle={{
        maxHeight: 'calc(100vh - 200px)',
        overflowY: 'auto',
        padding: '24px',
        background: THEME_COLORS.DARK.bg.secondary,
        color: THEME_COLORS.DARK.text.secondary,
        lineHeight: 1.8
      }}
    >
      <div style={{ color: THEME_COLORS.DARK.text.secondary, lineHeight: 1.8 }}>
        <Paragraph style={{ fontSize: 15, marginBottom: 24, color: THEME_COLORS.DARK.text.secondary }}>
          Welcome to Hooptie ("Hooptie.io," "we," "us," or "our").
        </Paragraph>

        <Paragraph style={{ fontSize: 15, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          These Terms of Service ("Terms") govern your access to and use of our website, platform, mobile application, and related services (collectively, the "Services").
        </Paragraph>

        <Paragraph style={{ fontSize: 15, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          By creating an account or using Hooptie.io, you agree to these Terms.
        </Paragraph>

        <Paragraph style={{ fontSize: 15, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary, fontWeight: 500 }}>
          If you do not agree, please do not use the Services.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          1. Eligibility
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          To use Hooptie, you must:
        </Paragraph>
        <ul style={{ fontSize: 14, marginBottom: 32, paddingLeft: 24, color: THEME_COLORS.DARK.text.secondary }}>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Be at least 18 years old</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Have the legal authority to represent your dealership or business</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Agree to comply with all applicable laws and these Terms</li>
        </ul>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          2. Description of Services
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          Hooptie provides data-driven tools for independent dealerships, including:
        </Paragraph>
        <ul style={{ fontSize: 14, marginBottom: 24, paddingLeft: 24, color: THEME_COLORS.DARK.text.secondary }}>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Market insights for active and sold vehicles</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Pricing recommendations and competitive analysis</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>AI-powered sourcing and profitability insights</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Vehicle history reports (via third-party partners)</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Dealer inventory management features</li>
        </ul>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          Hooptie does not buy or sell vehicles, handle financing, negotiate transactions, or guarantee sales performance.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          3. Account Registration
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          You must create an account to access most features. You agree to:
        </Paragraph>
        <ul style={{ fontSize: 14, marginBottom: 24, paddingLeft: 24, color: THEME_COLORS.DARK.text.secondary }}>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Provide accurate and current information</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Keep your login credentials secure</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Notify us immediately of unauthorized access</li>
        </ul>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          You are responsible for all activity under your account.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          4. Subscription & Billing
        </Title>

        <Title level={5} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 24, marginBottom: 12, fontSize: '1.1rem' }}>
          4.1 Subscription Plans
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 24, color: THEME_COLORS.DARK.text.secondary }}>
          Some features require a paid subscription. Plan details and pricing are available during signup or within your dashboard.
        </Paragraph>

        <Title level={5} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 24, marginBottom: 12, fontSize: '1.1rem' }}>
          4.2 Billing
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 24, color: THEME_COLORS.DARK.text.secondary }}>
          Payments are processed securely by Stripe or similar providers. By subscribing, you authorize Hooptie to charge your payment method on a recurring basis until canceled.
        </Paragraph>

        <Title level={5} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 24, marginBottom: 12, fontSize: '1.1rem' }}>
          4.3 Cancellation
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 24, color: THEME_COLORS.DARK.text.secondary }}>
          You may cancel your subscription at any time through your account settings or by contacting support. Canceling stops future charges but does not refund past payments unless required by law.
        </Paragraph>

        <Title level={5} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 24, marginBottom: 12, fontSize: '1.1rem' }}>
          4.4 Free Trials & Pro
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          If you access Hooptie through a free trial or Pro program, your usage is still governed by these Terms.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          5. Acceptable Use
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          You agree not to:
        </Paragraph>
        <ul style={{ fontSize: 14, marginBottom: 24, paddingLeft: 24, color: THEME_COLORS.DARK.text.secondary }}>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Use the platform for unlawful, fraudulent, or harmful activities</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Scrape, copy, or redistribute Hooptie's data without permission</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Reverse engineer or attempt to bypass security or rate limits</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Misrepresent vehicle information or violate dealer disclosure laws</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Use data from Hooptie to build competing services without written approval</li>
        </ul>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          We may suspend or terminate accounts that violate these rules.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          6. Market Data & AI Insights
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          Hooptie provides insights based on third-party data, dealership uploads, and automated analysis. You acknowledge that:
        </Paragraph>
        <ul style={{ fontSize: 14, marginBottom: 24, paddingLeft: 24, color: THEME_COLORS.DARK.text.secondary }}>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Data may contain inaccuracies or incomplete records</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>AI-generated insights are informational and not financial or legal advice</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Hooptie is not responsible for buying, pricing, or sales outcomes</li>
        </ul>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          You agree to use the data at your own discretion.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          7. Third-Party Services
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          Hooptie integrates with external providers such as:
        </Paragraph>
        <ul style={{ fontSize: 14, marginBottom: 24, paddingLeft: 24, color: THEME_COLORS.DARK.text.secondary }}>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Vehicle data APIs</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>VIN decoding services</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Vehicle history report providers</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Payment processors</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>AI processing services</li>
        </ul>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          Your use of these services may be subject to additional third-party terms.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          8. Intellectual Property
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          All content, features, software, branding, analytics, and data provided by Hooptie remain the exclusive property of Hooptie, LLC.
        </Paragraph>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          You receive a limited, non-transferable, revocable license to use the platform for your dealership's internal business purposes.
        </Paragraph>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          You may not reproduce, modify, sell, or distribute any part of Hooptie without written approval.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          9. Termination
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          We may suspend or terminate your account if:
        </Paragraph>
        <ul style={{ fontSize: 14, marginBottom: 24, paddingLeft: 24, color: THEME_COLORS.DARK.text.secondary }}>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>You violate these Terms</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>You misuse platform data</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>You fail to pay subscription fees</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>We discontinue the Services</li>
        </ul>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          You may request account deletion at any time via{' '}
          <a href="mailto:support@hooptie.io" style={{ color: THEME_COLORS.DARK.text.primary, textDecoration: 'underline' }}>
            support@hooptie.io
          </a>
          .
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          10. Disclaimer of Warranties
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          The Services are provided "as is" and "as available" without warranties of any kind, including accuracy, completeness, or fitness for a particular purpose.
        </Paragraph>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          We do not guarantee sales performance, market outcomes, pricing accuracy, or availability of all vehicle data.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          11. Limitation of Liability
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          To the maximum extent permitted by law, Hooptie is not liable for:
        </Paragraph>
        <ul style={{ fontSize: 14, marginBottom: 24, paddingLeft: 24, color: THEME_COLORS.DARK.text.secondary }}>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Loss of profits or revenue</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Business interruptions</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Decisions made using AI insights or market data</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Errors in third-party data</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Any indirect, incidental, or consequential damages</li>
        </ul>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          Hooptie's total liability for any claim is limited to the amount you paid in the past 3 months.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          12. Indemnification
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          You agree to indemnify and hold harmless Hooptie, LLC from claims, damages, or losses arising from:
        </Paragraph>
        <ul style={{ fontSize: 14, marginBottom: 24, paddingLeft: 24, color: THEME_COLORS.DARK.text.secondary }}>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Your misuse of the platform</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Your violation of laws or these Terms</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Any incorrect data you upload or share</li>
        </ul>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          13. Changes to These Terms
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          We may update these Terms periodically. If changes are significant, we will notify you via email or dashboard. Continued use of the Services indicates acceptance of the updated Terms.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          14. Governing Law
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          These Terms are governed by the laws of the State of Delaware (where Hooptie, LLC is formed), without regard to conflict of law principles.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          15. Contact Us
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          If you have questions about these Terms or your account:
        </Paragraph>
        <div style={{ fontSize: 14, marginBottom: 32, paddingLeft: 8 }}>
          <Paragraph style={{ marginBottom: 4, fontWeight: 500, color: THEME_COLORS.DARK.text.primary }}>Hooptie, LLC</Paragraph>
          <Paragraph style={{ marginBottom: 4, color: THEME_COLORS.DARK.text.secondary }}>
            Email:{' '}
            <a href="mailto:support@hooptie.io" style={{ color: THEME_COLORS.DARK.text.primary, textDecoration: 'underline' }}>
              support@hooptie.io
            </a>
          </Paragraph>
          <Paragraph style={{ marginBottom: 4, color: THEME_COLORS.DARK.text.secondary }}>
            Website:{' '}
            <a href="https://hooptie.io" target="_blank" rel="noopener noreferrer" style={{ color: THEME_COLORS.DARK.text.primary, textDecoration: 'underline' }}>
              https://hooptie.io
            </a>
          </Paragraph>
        </div>
      </div>
    </Modal>
  );
};

export default TermsOfServiceModal;

